import { z } from "zod";

import { MeetingNoteVisibility } from "@performyard/models";
import type {
	ActionItemModelJson,
	ActionItemSearchServiceFrontendResponse,
	AgendaItemModelJson,
	CreateActionItemRequestJson,
	CreateAgendaItemRequestJson,
	CreateMeetingRequestJson,
	CreateMeetingWorkspaceRequestJson,
	CreateMeetingWorkspaceResponseJson,
	ExportCompanyMeetingsRequestJson,
	ExportCompanyMeetingsResponseJson,
	ExportWorkspaceMeetingsRequestJson,
	ExportWorkspaceMeetingsResponseJson,
	ExtendedWorkspace,
	GetCarryOverItemsResponseFrontend,
	GetCarryOverItemsResponseJson,
	GetMeetingDetailsResponseJson,
	GetMeetingWorkspacesSearchServiceFrontendResponse,
	GetMeetingWorkspacesSearchServiceJsonResponse,
	GetMeetingsSearchServiceFrontendResponse,
	GetMeetingsSearchServiceJsonResponse,
	GetUpcomingMeetingsResponseFrontend,
	GetUpcomingMeetingsResponseJson,
	MeetingCalendarOperation,
	MeetingModelJson,
	MeetingNoteModelJson,
	UpdateActionItemRequestJson,
	UpdateAgendaItemRequestJson,
	UpdateMeetingNoteRequestJson,
	UpdateMeetingRequestJson,
	UpdateMeetingWorkspaceRequestJson,
	UpdateMeetingWorkspaceResponseJson,
} from "@performyard/service-contracts";

import request from "./internal-base-request";

export const postMeetingWorkspace = async (
	data: CreateMeetingWorkspaceRequestJson
): Promise<CreateMeetingWorkspaceResponseJson> => {
	const [response] = await request<CreateMeetingWorkspaceResponseJson>("/v1/meetings/workspaces", {
		method: "POST",
		body: JSON.stringify(data),
	});

	return response;
};

export const patchMeetingWorkspace = async (
	workspaceId: string,
	data: UpdateMeetingWorkspaceRequestJson
): Promise<UpdateMeetingWorkspaceResponseJson> => {
	const [response] = await request<UpdateMeetingWorkspaceResponseJson>(`/v1/meetings/workspaces/${workspaceId}`, {
		method: "PATCH",
		body: JSON.stringify(data),
	});

	return response;
};

export const getMeetingWorkspaceDetails = async (workspaceId: string) => {
	const [data] = await request<ExtendedWorkspace>(`/v1/meetings/workspaces/${workspaceId}`);

	const formattedData: ExtendedWorkspace = {
		...data,
		created: {
			...data.created,
			timeStamp: new Date(data.created.timeStamp),
		},
		updated: {
			...data.updated,
			timeStamp: new Date(data.updated.timeStamp),
		},
	};

	return [formattedData];
};

export const addWorkspaceMeeting = async (workspaceId: string, data: CreateMeetingRequestJson) => {
	const response = await request<MeetingModelJson>(`/v1/meetings/workspaces/${workspaceId}/meetings`, {
		method: "POST",
		body: JSON.stringify({
			...data,
		}),
	});
	return response?.[0];
};

const WorkspacesSearchSortableFieldsEnum = z.enum(["name", "creator", "previousMeeting", "upcomingMeeting", "status"]);
export type WorkspacesSearchSortableFields = z.infer<typeof WorkspacesSearchSortableFieldsEnum>;

const GetMeetingWorkspacesParamsSchema = z.object({
	name: z.string().optional(),
	participantIds: z.array(z.string()).optional(),
	createdById: z.string().optional(),
	createdByIdNe: z.string().optional(),
	upcomingMeetingStartGte: z.string().optional(),
	upcomingMeetingStartLte: z.string().optional(),
	previousMeetingStartGte: z.string().optional(),
	previousMeetingStartLte: z.string().optional(),
	statusEq: z.string().optional(),
	statusIn: z.array(z.string()).optional(),

	limit: z.number().optional(),
	skip: z.number().optional(),
	sort: z
		.object({
			name: WorkspacesSearchSortableFieldsEnum,
			direction: z.enum(["asc", "desc"]),
		})
		.optional(),
});

export type GetMeetingWorkspacesParams = z.infer<typeof GetMeetingWorkspacesParamsSchema>;

// Column names need to be mapped to search service property names
const workspacesSearchSortMap: Record<WorkspacesSearchSortableFields, string> = {
	name: "name",
	creator: "created_by_name",
	previousMeeting: "previous_meeting_start_date_time",
	upcomingMeeting: "upcoming_meeting_start_date_time",
	status: "status",
};

/**
 * Retrieves meeting workspaces using the internal-api search service..
 *
 * @param {GetMeetingWorkspacesParams} params - The parameters for filtering and sorting the meeting workspaces.
 * @returns {Promise<GetMeetingWorkspacesSearchServiceFrontendResponse>} A promise that resolves to the frontend response containing meeting workspaces.
 */
export const getMeetingWorkspacesSearch = async (
	params: GetMeetingWorkspacesParams
): Promise<GetMeetingWorkspacesSearchServiceFrontendResponse> => {
	const validatedParams = GetMeetingWorkspacesParamsSchema.parse(params);
	const baseUrl = "/v1/search";
	const searchParams = new URLSearchParams();

	searchParams.append("service", "meeting_workspace");

	// Filters
	if (validatedParams.name !== undefined) {
		searchParams.append("name_re", validatedParams.name);
	}
	if (validatedParams.participantIds !== undefined) {
		for (const participantId of validatedParams.participantIds) {
			searchParams.append("participant_id_all", participantId);
		}
	}
	if (validatedParams.createdById !== undefined) {
		searchParams.append("created_by_id_eq", validatedParams.createdById);
	}
	if (validatedParams.createdByIdNe !== undefined) {
		searchParams.append("created_by_id_ne", validatedParams.createdByIdNe);
	}
	if (validatedParams.upcomingMeetingStartGte !== undefined) {
		searchParams.append("upcoming_meeting_start_date_time_gte", validatedParams.upcomingMeetingStartGte);
	}
	if (validatedParams.upcomingMeetingStartLte !== undefined) {
		searchParams.append("upcoming_meeting_start_date_time_lte", validatedParams.upcomingMeetingStartLte);
	}
	if (validatedParams.previousMeetingStartGte !== undefined) {
		searchParams.append("previous_meeting_start_date_time_gte", validatedParams.previousMeetingStartGte);
	}
	if (validatedParams.previousMeetingStartLte !== undefined) {
		searchParams.append("previous_meeting_start_date_time_lte", validatedParams.previousMeetingStartLte);
	}
	if (validatedParams.statusEq !== undefined) {
		searchParams.append("status_eq", validatedParams.statusEq);
	}
	if (validatedParams.statusIn !== undefined) {
		for (const status of validatedParams.statusIn) {
			searchParams.append("status_in", status);
		}
	}

	// If skip and/or limit provided, append to query
	if (validatedParams.limit !== undefined) searchParams.append("limit", validatedParams.limit.toString());
	if (validatedParams.skip !== undefined) searchParams.append("skip", validatedParams.skip.toString());

	// If custom sort provided, map sort parameter and append to query
	// Otherwise, default to upcoming_meeting_start_date_time ascending
	if (validatedParams.sort) {
		const { name, direction } = validatedParams.sort;
		const sortName = workspacesSearchSortMap[name] || name;
		searchParams.append("sort", `${direction}(${sortName})`);
	} else {
		const defaultSort = "asc(upcoming_meeting_start_date_time)";
		searchParams.append("sort", defaultSort);
	}

	const queryString = searchParams.toString() ? `?${searchParams.toString()}` : "";
	const [meetingWorkspacesResponse] = await request<GetMeetingWorkspacesSearchServiceJsonResponse>(
		`${baseUrl}${queryString}`
	);

	return {
		...meetingWorkspacesResponse,
		results: meetingWorkspacesResponse.results.map((meetingWorkspace) => ({
			...meetingWorkspace,
			created: {
				...meetingWorkspace.created,
				timeStamp: new Date(meetingWorkspace.created.timeStamp),
			},
			updated: {
				...meetingWorkspace.updated,
				timeStamp: new Date(meetingWorkspace.updated.timeStamp),
			},
			previousMeetingStartDateTime: meetingWorkspace.previousMeetingStartDateTime
				? new Date(meetingWorkspace.previousMeetingStartDateTime)
				: undefined,
			upcomingMeetingStartDateTime: meetingWorkspace.upcomingMeetingStartDateTime
				? new Date(meetingWorkspace.upcomingMeetingStartDateTime)
				: undefined,
		})),
	};
};

const MeetingsSearchWorkspacesSearchSortableFieldsEnum = z.enum([
	"name",
	"startDateTime",
	"meetingWorkspaceName",
	"meetingDate",
	"status",
	"agendaItems",
	"actionItems",
]);
export type MeetingsSearchSortableFields = z.infer<typeof MeetingsSearchWorkspacesSearchSortableFieldsEnum>;

const GetMeetingsSearchParamsSchema = z.object({
	meetingWorkspaceId: z.string().optional(),

	participantId: z.string().optional(),
	status: z.array(z.string()).optional(),
	dateRangeStart: z.date().optional(),
	dateRangeEnd: z.date().optional(),
	name: z.string().optional(),
	isCompanyMeetingsRequest: z.boolean().optional(),
	privateNotesFilter: z.string().optional(),

	limit: z.number().optional(),
	skip: z.number().optional(),
	sort: z
		.object({
			name: MeetingsSearchWorkspacesSearchSortableFieldsEnum,
			direction: z.enum(["asc", "desc"]),
		})
		.optional(),
});

export type GetMeetingsSearchParams = z.infer<typeof GetMeetingsSearchParamsSchema>;

// Column names need to be mapped to search service property names
const meetingsSearchSortMap: Record<MeetingsSearchSortableFields, string> = {
	name: "name",
	startDateTime: "start_date_time",
	meetingWorkspaceName: "meeting_workspace_name",
	meetingDate: "start_date_time",
	status: "status",
	agendaItems: "agenda_item_count",
	actionItems: "action_item_count",
};

/**
 * Retrieves meetings using the internal-api search service..
 *
 * @param {GetMeetingsSearchParams} params - The parameters for filtering and sorting the meetings.
 * @returns {Promise<GetMeetingsSearchServiceFrontendResponse>} A promise that resolves to the frontend response containing meetings.
 */
export const getMeetingsSearch = async (
	params: GetMeetingsSearchParams
): Promise<GetMeetingsSearchServiceFrontendResponse> => {
	const validatedParams = GetMeetingsSearchParamsSchema.parse(params);
	const baseUrl = "/v1/search";
	const searchParams = new URLSearchParams();

	searchParams.append("service", "meeting");

	// Function to append each parameter to the query
	const appendParam = ({
		key,
		queryType,
		value,
	}: {
		key: string;
		queryType?: "eq" | "re" | "lt" | "gt" | "in" | "gte" | "lte";
		value: string;
	}) => {
		if (value) {
			if (queryType) {
				searchParams.append(`${key}_${queryType}`, value);
			} else {
				searchParams.append(key, value);
			}
		}
	};

	// Filters
	if (validatedParams.meetingWorkspaceId !== undefined) {
		appendParam({
			key: "meeting_workspace_id",
			queryType: "eq",
			value: validatedParams.meetingWorkspaceId,
		});
	}

	if (validatedParams.participantId !== undefined) {
		appendParam({
			key: "participant_id",
			queryType: "eq",
			value: validatedParams.participantId,
		});
	}

	if (validatedParams.status !== undefined) {
		appendParam({
			key: "status",
			queryType: "in",
			value: validatedParams.status.join(","),
		});
	}

	if (validatedParams.dateRangeStart !== undefined) {
		appendParam({
			key: "start_date_time",
			queryType: "gte",
			value: validatedParams.dateRangeStart.toISOString(),
		});
	}

	if (validatedParams.dateRangeEnd !== undefined) {
		appendParam({
			key: "start_date_time",
			queryType: "lte",
			value: validatedParams.dateRangeEnd.toISOString(),
		});
	}

	if (validatedParams.name !== undefined && validatedParams.name.trim() !== "") {
		appendParam({
			key: "name",
			queryType: "re",
			value: validatedParams.name.trim(),
		});
	}

	if (validatedParams.isCompanyMeetingsRequest !== undefined) {
		appendParam({
			key: "is_company_meetings_request",
			value: validatedParams.isCompanyMeetingsRequest ? "true" : "false",
		});
	}

	if (validatedParams.privateNotesFilter !== undefined) {
		appendParam({
			key: "private_notes_filter",
			queryType: "eq",
			value: validatedParams.privateNotesFilter,
		});
	}

	// If skip and/or limit provided, append to query
	if (validatedParams.limit !== undefined) {
		appendParam({
			key: "limit",
			value: validatedParams.limit.toString(),
		});
	}

	if (validatedParams.skip !== undefined) {
		appendParam({
			key: "skip",
			value: validatedParams.skip.toString(),
		});
	}

	// If custom sort provided, map sort parameter and append to query
	// Otherwise, default to start_date_time descending
	if (validatedParams.sort) {
		const { name, direction } = validatedParams.sort;
		const sortName = meetingsSearchSortMap[name] || name;

		// For meetingDate (which maps to start_date_time), add a secondary sort by name
		if (name === "meetingDate") {
			appendParam({
				key: "sort",
				value: `${direction}(${sortName}),asc(name)`,
			});
		} else {
			appendParam({
				key: "sort",
				value: `${direction}(${sortName})`,
			});
		}
	} else {
		// Default sort with secondary sort by name
		const defaultSort = "desc(start_date_time),asc(name)";
		appendParam({
			key: "sort",
			value: defaultSort,
		});
	}

	const queryString = searchParams.toString() ? `?${searchParams.toString()}` : "";
	const [meetingsResponse] = await request<GetMeetingsSearchServiceJsonResponse>(`${baseUrl}${queryString}`);

	return {
		...meetingsResponse,
		results: meetingsResponse.results.map((meeting) => ({
			...meeting,
			startDateTime: new Date(meeting.startDateTime),
			endDateTime: new Date(meeting.endDateTime),
			created: {
				...meeting.created,
				timeStamp: new Date(meeting.created.timeStamp),
			},
			updated: {
				...meeting.updated,
				timeStamp: new Date(meeting.updated.timeStamp),
			},
		})),
	};
};

export const getMeetingAgendaItems = async (meetingId: string) => {
	const [data] = await request<AgendaItemModelJson[]>(`/v1/meetings/meetings/${meetingId}/agenda-items`);

	const formattedData = data.map((agendaItem) => {
		return {
			...agendaItem,
			created: {
				...agendaItem.created,
				timeStamp: new Date(agendaItem.created.timeStamp),
			},
			updated: {
				...agendaItem.updated,
				timeStamp: new Date(agendaItem.updated.timeStamp),
			},
		};
	});

	return [formattedData];
};

export const getMeetingActionItems = async (meetingId: string) => {
	const [data] = await request<ActionItemModelJson[]>(`/v1/meetings/meetings/${meetingId}/action-items`);

	const formattedData = data.map((actionItem) => {
		return {
			...actionItem,
			created: {
				...actionItem.created,
				timeStamp: new Date(actionItem.created.timeStamp),
			},
			updated: {
				...actionItem.updated,
				timeStamp: new Date(actionItem.updated.timeStamp),
			},
			dueDate: actionItem.dueDate ? new Date(actionItem.dueDate) : null,
		};
	});

	return [formattedData];
};

export const getMeetingNotes = async (meetingId: string, visibility?: MeetingNoteVisibility) => {
	const queryParams = visibility ? `?visibility=${visibility}` : "";

	const [data] = await request<MeetingNoteModelJson[]>(`/v1/meetings/meetings/${meetingId}/notes${queryParams}`);

	const formattedData = data.map((note) => {
		return {
			...note,
			updated: {
				...note.updated,
				timeStamp: new Date(note.updated.timeStamp),
			},
		};
	});

	return [formattedData];
};

export const editWorkspaceMeeting = async (meetingId: string, data: UpdateMeetingRequestJson) => {
	const response = await request<MeetingModelJson & { calendarOperationType: MeetingCalendarOperation }>(
		`/v1/meetings/meetings/${meetingId}`,
		{
			method: "PATCH",
			body: JSON.stringify({
				...data,
			}),
		}
	);
	return response?.[0];
};

export const getLatestMeetingCarryOverItems = async (
	workspaceId: string,
	startDateTime: Date
): Promise<GetCarryOverItemsResponseFrontend> => {
	const queryString = new URLSearchParams({ startDateTime: startDateTime.toISOString() });

	const [data] = await request<GetCarryOverItemsResponseJson>(
		`/v1/meetings/workspaces/${workspaceId}/carry-over-items?${queryString}`
	);

	return data;
};

export const addMeetingAgendaItem = async (data: CreateAgendaItemRequestJson) => {
	const response = await request<AgendaItemModelJson>(`/v1/meetings/agenda-items`, {
		method: "POST",
		body: JSON.stringify({
			...data,
		}),
	});
	return response?.[0];
};

export const editMeetingAgendaItem = async (agendaItemId: string, data: UpdateAgendaItemRequestJson) => {
	const response = await request<AgendaItemModelJson>(`/v1/meetings/agenda-items/${agendaItemId}`, {
		method: "PATCH",
		body: JSON.stringify({
			...data,
		}),
	});
	return response?.[0];
};

export const addMeetingActionItem = async (data: CreateActionItemRequestJson) => {
	const response = await request<ActionItemModelJson>(`/v1/meetings/action-items`, {
		method: "POST",
		body: JSON.stringify({
			...data,
		}),
	});
	return response?.[0];
};

export const editMeetingActionItem = async (actionItemId: string, data: UpdateActionItemRequestJson) => {
	const response = await request<ActionItemModelJson>(`/v1/meetings/action-items/${actionItemId}`, {
		method: "PATCH",
		body: JSON.stringify({
			...data,
		}),
	});
	return response?.[0];
};

export const updatePublicMeetingNotes = async (noteId: string, data: UpdateMeetingNoteRequestJson) => {
	const response = await request<MeetingNoteModelJson>(`/v1/meetings/public-notes/${noteId}`, {
		method: "PATCH",
		body: JSON.stringify({
			...data,
		}),
	});
	return response?.[0];
};

export const updatePrivateMeetingNotes = async (noteId: string, data: UpdateMeetingNoteRequestJson) => {
	const response = await request<MeetingNoteModelJson>(`/v1/meetings/private-notes/${noteId}`, {
		method: "PATCH",
		body: JSON.stringify({
			...data,
		}),
	});
	return response?.[0];
};

export const exportCompanyMeetingsCsv = async (
	params: ExportCompanyMeetingsRequestJson
): Promise<ExportCompanyMeetingsResponseJson> => {
	const [response] = await request<ExportCompanyMeetingsResponseJson>("/v1/meetings/export-csv", {
		method: "POST",
		body: JSON.stringify(params),
	});

	return response;
};

export const exportWorkspaceMeetingsCsv = async (
	workspaceId: string,
	params: ExportWorkspaceMeetingsRequestJson
): Promise<ExportWorkspaceMeetingsResponseJson> => {
	const [response] = await request<ExportWorkspaceMeetingsResponseJson>(
		`/v1/meetings/workspaces/${workspaceId}/export-csv`,
		{
			method: "POST",
			body: JSON.stringify(params),
		}
	);

	return response;
};

export const getMeetingDetails = async (meetingId: string) => {
	const [response] = await request<GetMeetingDetailsResponseJson>(`/v1/meetings/meetings/${meetingId}`, {
		method: "GET",
	});

	return {
		meeting: {
			...response.meeting,
			startDateTime: new Date(response.meeting.startDateTime),
			endDateTime: new Date(response.meeting.endDateTime),
			created: {
				...response.meeting.created,
				timeStamp: new Date(response.meeting.created.timeStamp),
			},
			updated: {
				...response.meeting.updated,
				timeStamp: new Date(response.meeting.updated.timeStamp),
			},
			calendarEvent: response.meeting.calendarEvent
				? {
						...response.meeting.calendarEvent,
						created: new Date(response.meeting.calendarEvent.created),
						updated: new Date(response.meeting.calendarEvent.updated),
					}
				: undefined,
		},
		workspace: {
			...response.workspace,
			created: {
				...response.workspace.created,
				timeStamp: new Date(response.workspace.created.timeStamp),
			},
			updated: {
				...response.workspace.updated,
				timeStamp: new Date(response.workspace.updated.timeStamp),
			},
			previousMeetingStartDateTime: response.workspace.previousMeetingStartDateTime
				? new Date(response.workspace.previousMeetingStartDateTime)
				: undefined,
			upcomingMeetingStartDateTime: response.workspace.upcomingMeetingStartDateTime
				? new Date(response.workspace.upcomingMeetingStartDateTime)
				: undefined,
		},
	};
};

export type GetUpcomingMeetingsParams = {
	startDate: Date;
	endDate: Date;
};

export const getUpcomingMeetings = async (
	params: GetUpcomingMeetingsParams
): Promise<GetUpcomingMeetingsResponseFrontend> => {
	const searchParams = new URLSearchParams({
		startDate: params.startDate.toISOString(),
		endDate: params.endDate.toISOString(),
	});

	const [response] = await request<GetUpcomingMeetingsResponseJson>(
		`/v1/meetings/upcoming?${searchParams.toString()}`
	);

	return response.map((meeting) => ({
		...meeting,
		startDateTime: new Date(meeting.startDateTime),
		endDateTime: new Date(meeting.endDateTime),
	}));
};

const ActionItemsSearchSortableFieldsEnum = z.enum([
	"name",
	"dueDate",
	"status",
	"created.timeStamp",
	"assignee",
	"createdBy",
	"workspaceName",
]);
export type ActionItemsSearchSortableFields = z.infer<typeof ActionItemsSearchSortableFieldsEnum>;

const GetActionItemsSearchParamsSchema = z.object({
	name: z.string().optional(),
	createdByUserId: z.string().optional(),
	assigneeUserId: z.string().optional(),
	isUnassigned: z.string().optional(),
	statusEq: z.string().optional(),
	dueDateStart: z.string().optional(),
	dueDateEnd: z.string().optional(),
	workspaceId: z.string().optional(),

	limit: z.number().optional(),
	skip: z.number().optional(),
	sort: z
		.object({
			name: ActionItemsSearchSortableFieldsEnum,
			direction: z.enum(["asc", "desc"]),
		})
		.optional(),
});

export type GetActionItemsSearchParams = z.infer<typeof GetActionItemsSearchParamsSchema>;

// Column names need to be mapped to search service property names
const actionItemsSearchSortMap: Record<ActionItemsSearchSortableFields, string> = {
	name: "name",
	dueDate: "due_date",
	status: "most_recent_meeting_status",
	"created.timeStamp": "created.time_stamp",
	assignee: "assignee",
	createdBy: "created_by",
	workspaceName: "workspace_name",
};

/**
 * Retrieves action items using the internal-api search service.
 *
 * @param {GetActionItemsSearchParams} params - The parameters for filtering and sorting the action items.
 * @returns {Promise<ActionItemSearchServiceFrontendResponse>} A promise that resolves to the frontend response containing action items.
 */
export const getActionItemsSearch = async (
	params: GetActionItemsSearchParams
): Promise<ActionItemSearchServiceFrontendResponse> => {
	const validatedParams = GetActionItemsSearchParamsSchema.parse(params);
	const baseUrl = "/v1/search";
	const searchParams = new URLSearchParams();

	searchParams.append("service", "action_item");

	// Filters
	if (validatedParams.name) {
		searchParams.append("name_re", validatedParams.name);
	}
	if (validatedParams.createdByUserId) {
		searchParams.append("created_user_id_eq", validatedParams.createdByUserId);
	}
	if (validatedParams.assigneeUserId) {
		searchParams.append("participants_user_id_eq", validatedParams.assigneeUserId);
	}
	if (validatedParams.isUnassigned) {
		searchParams.append("is_unassigned_eq", validatedParams.isUnassigned);
	}
	if (validatedParams.statusEq) {
		searchParams.append("most_recent_meeting_status_eq", validatedParams.statusEq);
	}
	if (validatedParams.dueDateStart) {
		searchParams.append("due_date_gte", validatedParams.dueDateStart);
	}
	if (validatedParams.dueDateEnd) {
		searchParams.append("due_date_lte", validatedParams.dueDateEnd);
	}
	if (validatedParams.workspaceId) {
		searchParams.append("workspace_id_eq", validatedParams.workspaceId);
	}

	// Pagination
	if (validatedParams.limit) {
		searchParams.append("limit", validatedParams.limit.toString());
	}
	if (validatedParams.skip) {
		searchParams.append("skip", validatedParams.skip.toString());
	}

	// Sort
	if (validatedParams.sort) {
		const { name, direction } = validatedParams.sort;
		const sortName = actionItemsSearchSortMap[name] || name;
		searchParams.append("sort", `${direction}(${sortName})`);
	} else {
		const defaultSort = "desc(created.time_stamp)";
		searchParams.append("sort", defaultSort);
	}

	const queryString = searchParams.toString() ? `?${searchParams.toString()}` : "";
	const [actionItemsResponse] = await request<ActionItemSearchServiceFrontendResponse>(`${baseUrl}${queryString}`);

	return {
		...actionItemsResponse,
		results: actionItemsResponse.results.map((actionItem) => ({
			...actionItem,
			created: {
				...actionItem.created,
				timeStamp: new Date(actionItem.created.timeStamp),
			},
			updated: {
				...actionItem.updated,
				timeStamp: new Date(actionItem.updated.timeStamp),
			},
			dueDate: actionItem.dueDate ? new Date(actionItem.dueDate) : null,
			mostRecentMeetingStartDateTime: new Date(actionItem.mostRecentMeetingStartDateTime),
		})),
	};
};
